// Schema.org JSON-LD helpers + simple meta tag injection for SEO.

export function localBusinessSchema(loc, brand) {
    return {
        "@context": "https://schema.org",
        "@type": "CannabisStore",
        name: loc.name,
        image: loc.image,
        telephone: loc.phone,
        priceRange: "$$",
        address: {
            "@type": "PostalAddress",
            streetAddress: loc.street,
            addressLocality: loc.city,
            addressRegion: loc.state,
            postalCode: loc.zip,
            addressCountry: "US",
        },
        geo: {
            "@type": "GeoCoordinates",
            latitude: loc.lat,
            longitude: loc.lng,
        },
        url: loc.store_url,
        sameAs: [brand?.store_url || "https://zenzest.com"],
        aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: loc.rating,
            reviewCount: loc.review_count,
        },
        openingHoursSpecification: (loc.hours || []).map((h) => ({
            "@type": "OpeningHoursSpecification",
            dayOfWeek: expandDay(h.day),
            opens: to24h(h.open),
            closes: to24h(h.close),
        })),
    };
}

export function organizationSchema(brand) {
    return {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: brand?.name || "ZenZest Cannabis",
        url: brand?.store_url || "https://zenzest.com",
        logo: brand?.logo,
        slogan: brand?.tagline,
        aggregateRating: brand && {
            "@type": "AggregateRating",
            ratingValue: brand.rating,
            reviewCount: brand.reviews,
        },
    };
}

export function breadcrumbSchema(items) {
    return {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: items.map((it, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: it.name,
            item: it.url,
        })),
    };
}

export function articleSchema({ headline, description, url, image, datePublished, dateModified }) {
    return {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: headline?.slice(0, 110),
        description,
        image: image || "https://weeddispensarynearme.com/zenzest-badge.png",
        mainEntityOfPage: { "@type": "WebPage", "@id": url },
        url,
        datePublished: datePublished || dateModified,
        dateModified: dateModified || datePublished,
        author: {
            "@type": "Organization",
            name: "ZenZest Editorial Team",
            url: "https://weeddispensarynearme.com/",
            description: "Licensed NY OCM cannabis retailers — reviewed by ZenZest budtenders at our Queens and Staten Island dispensaries.",
        },
        publisher: {
            "@type": "Organization",
            name: "ZenZest",
            url: "https://weeddispensarynearme.com/",
            logo: {
                "@type": "ImageObject",
                url: "https://weeddispensarynearme.com/zenzest-badge.png",
            },
        },
    };
}

export function blogPostingSchema(post, brand) {
    return {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        headline: post.title,
        image: post.image,
        author: { "@type": "Organization", name: post.author },
        datePublished: post.date,
        description: post.excerpt,
        publisher: {
            "@type": "Organization",
            name: brand?.name || "ZenZest Cannabis",
            logo: { "@type": "ImageObject", url: brand?.logo },
        },
    };
}

function to24h(t) {
    // "9 AM" / "12 AM" / "2 AM" / "10 PM"
    if (!t) return "00:00";
    const match = t.trim().match(/^(\d+)(?::(\d+))?\s*(AM|PM)$/i);
    if (!match) return "00:00";
    let h = parseInt(match[1], 10);
    const m = match[2] ? parseInt(match[2], 10) : 0;
    const ampm = match[3].toUpperCase();
    if (ampm === "PM" && h !== 12) h += 12;
    if (ampm === "AM" && h === 12) h = 0;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function expandDay(short) {
    const map = {
        Mon: "Monday",
        Tue: "Tuesday",
        Wed: "Wednesday",
        Thu: "Thursday",
        Fri: "Friday",
        Sat: "Saturday",
        Sun: "Sunday",
    };
    return map[short] || short;
}

export function faqSchema(items) {
    return {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: (items || []).map((it) => ({
            "@type": "Question",
            name: it.q,
            acceptedAnswer: { "@type": "Answer", text: it.a },
        })),
    };
}

/**
 * Speakable schema — tells Google Assistant + Google AI Overview which
 * DOM regions are voice-answerable. Critical for AI search citation.
 * Pass an array of CSS selectors that identify the spoken-answer blocks
 * (typically the FAQ summary, neighborhood TL;DR, or strain blurb).
 */
export function speakableSchema(cssSelectors, url) {
    return {
        "@context": "https://schema.org",
        "@type": "WebPage",
        ...(url ? { url } : {}),
        speakable: {
            "@type": "SpeakableSpecification",
            cssSelector: Array.isArray(cssSelectors) ? cssSelectors : [cssSelectors],
        },
    };
}

/**
 * Quick Answer / TL;DR schema fragment — also helps AI Overview pick up
 * the short summary as a citation candidate. Wraps in a Question/Answer
 * pair targeting the canonical query for the page.
 */
export function quickAnswerSchema({ question, answer, url }) {
    return {
        "@context": "https://schema.org",
        "@type": "QAPage",
        ...(url ? { url } : {}),
        mainEntity: {
            "@type": "Question",
            name: question,
            answerCount: 1,
            acceptedAnswer: {
                "@type": "Answer",
                text: answer,
                ...(url ? { url } : {}),
            },
        },
    };
}

export function comparisonSchema({ name, competitor, url, rating, count }) {
    return {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: `ZenZest vs ${competitor} — NY Cannabis Dispensary Comparison`,
        about: [
            { "@type": "LocalBusiness", name: "ZenZest" },
            { "@type": "LocalBusiness", name: competitor },
        ],
        author: {
            "@type": "Organization",
            name: name || "weeddispensarynearme.com",
        },
        mainEntityOfPage: url,
        ...(rating && count
            ? {
                  aggregateRating: {
                      "@type": "AggregateRating",
                      ratingValue: Number(rating).toFixed(1),
                      reviewCount: count,
                  },
              }
            : {}),
    };
}

/**
 * WebPage schema — fragment that anchors a page in the site's WebSite +
 * Organization graph using the canonical @id of the static graph in
 * /public/index.html. Use this on top-level location-aware pages so
 * Google can join the dots without us having to repeat the parent
 * Organization inline (which previously triggered GSC "Invalid object
 * type for field <parent_node>" warnings).
 */
export function webPageSchema({ url, name }) {
    return {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "@id": `${url}#webpage`,
        url,
        name,
        isPartOf: {
            "@type": "WebSite",
            "@id": "https://weeddispensarynearme.com/#website",
            url: "https://weeddispensarynearme.com/",
            name: "Weed Dispensary Near Me",
        },
    };
}

/**
 * Article schema — YMYL editorial page metadata (brand guides, strain
 * guides, FAQ answers). Includes an Organization author (ZenZest Editorial
 * Team) which Google reads as a credentialed byline for cannabis content.
 * (Definition moved above; re-export declaration removed to avoid dup.)
 */

export function slugify(s) {
    return s
        .toLowerCase()
        .replace(/['.]/g, "")
        .replace(/\s+/g, "-")
        .replace(/--+/g, "-");
}
